import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PixelDragon64 } from './PixelDragon64';
import { theme } from '../theme';
import { assetUrl } from '../utils/assetUrl';

const Page = styled.div`
  min-height: 100vh;
`;

const TopBar = styled.header`
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
`;

const TopBarInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${theme.space[4]}px ${theme.space[5]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${theme.space[4]}px;
  position: relative;
  flex-wrap: wrap;
`;

const BrandGroup = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.space[3]}px;
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`;

const BrandIcon = styled.img`
  width: 32px;
  height: 32px;
  display: block;
  image-rendering: pixelated;
  image-rendering: crisp-edges;
`;

const BrandName = styled.span`
  display: flex;
  align-items: baseline;
  font-family: ${theme.fonts.display};
  font-size: ${theme.fontSizes.lg};
  font-weight: normal;
  color: ${theme.colors.text};
  letter-spacing: -0.01em;

  ${BrandGroup}:hover & {
    color: ${theme.colors.accent};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.space[5]}px;

  @media (max-width: 768px) {
    width: 100%;
    display: ${({ $open }) => ($open ? 'flex' : 'none')};
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.space[3]}px;
    margin-top: ${theme.space[2]}px;
    padding-top: ${theme.space[3]}px;
    border-top: 1px solid ${theme.colors.border};
  }
`;

const MenuButton = styled.button`
  display: none;
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.muted};
  border-radius: ${theme.radii.sm}px;
  padding: 6px 10px;
  font-size: ${theme.fontSizes.sm};
  line-height: 1;
  cursor: pointer;

  &:hover {
    color: ${theme.colors.accent};
    border-color: ${theme.colors.accent};
  }

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const NavLink = styled(Link)`
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.muted};
  text-decoration: none;

  &:hover {
    color: ${theme.colors.accent};
    text-decoration: none;
  }
`;

const ExternalNavLink = styled.a`
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.muted};
  text-decoration: none;

  &:hover {
    color: ${theme.colors.accent};
    text-decoration: none;
  }
`;

const Main = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${theme.space[5]}px ${theme.space[5]}px ${theme.space[8]}px;
`;

const Footer = styled.footer`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${theme.space[5]}px;
  border-top: 1px solid ${theme.colors.border};
  color: ${theme.colors.muted};
  font-size: ${theme.fontSizes.sm};
`;

export function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Page>
      <TopBar>
        <TopBarInner>
          <BrandGroup to="/">
            <BrandIcon src={assetUrl('/favicon.png')} alt="" />
            <BrandName>
              <PixelDragon64 />
            </BrandName>
          </BrandGroup>
          <MenuButton
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-label="Toggle menu"
          >
            ☰
          </MenuButton>
          <Nav $open={menuOpen}>
            <NavLink to="/portfolio" onClick={() => setMenuOpen(false)}>Portfolio</NavLink>
            <NavLink to="/characters" onClick={() => setMenuOpen(false)}>Characters</NavLink>
            <ExternalNavLink
              href="https://x.com/pixeldragon64"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
            >
              Twitter
            </ExternalNavLink>
          </Nav>
        </TopBarInner>
      </TopBar>
      <Main>{children}</Main>
      <Footer>
        © {new Date().getFullYear()} <PixelDragon64 />
      </Footer>
    </Page>
  );
}
